import React, {useState} from 'react'
import styled from '@emotion/styled'
import {
  OrderSummary as OrderSummaryComponent,
  DEFAULT_TABS,
} from '../../../../features/OrderSummary'
import {buildLocationOrderSummary} from '../../../../pages/CatalogSelection/utils/orderSummaryUtils'

// OrderSummary's own Wrapper is height: 100% + overflow-y: auto (sized for the
// Catalog Selection side panel). Here it sits in normal page flow, so let it
// grow to its full content and let the page scroll instead of showing an
// internal scrollbar while there's free space below.
const FullHeightSummary = styled.div`
  width: 100%;

  > div {
    height: auto;
    overflow: visible;
    border-radius: 12px;
  }
`

// Read-only recap of the order for Payment Details — deliberately NOT reusing
// OrderSummaryContainer/MultiLocationOrderSummaryContainer (Catalog
// Selection's editable versions): by this step the order_header/order_line
// rows are already written (see AccountSetup/index.js's handleCatalogContinue),
// so removing an item or editing coupons here would desync the visible cart
// from what was actually sent to the backend with no call to re-sync it.
// Renders the same presentational OrderSummary used everywhere else, just
// with every onRemove*/footer prop omitted so no edit affordances show.
export function PaymentDetailsOrderSummary({
  locations,
  orderMode,
  country,
  isUpgrade,
}) {
  const [activeTab, setActiveTab] = useState(DEFAULT_TABS[0]?.id)
  const [openLocations, setOpenLocations] = useState({0: true})
  const [softwareOpen, setSoftwareOpen] = useState({0: true})
  const [addonsOpen, setAddonsOpen] = useState({})
  const [oneTimeFeeOpen, setOneTimeFeeOpen] = useState({})
  const [couponsOpen, setCouponsOpen] = useState({})

  const isMonthlyTab = activeTab === 'monthly'
  const isParentPay = orderMode === 'parentPay'
  const dueTodayIncludesTax = locations.some(loc => !!loc.thryvId)

  const summaries = locations.map(loc =>
    buildLocationOrderSummary(loc.items, {isMonthlyTab}),
  )

  const renderLocations = locations.map((loc, i) => ({
    locationId: loc.locationId,
    name: loc.locationName || loc.thryvId || '',
    softwareItems: summaries[i].softwareItems,
    addonItems: summaries[i].addonItems,
    oneTimeFeeItems: summaries[i].oneTimeFeeItems,
    itemCount: summaries[i].itemCount,
    total: summaries[i].total,
    coupons: loc.coupons ?? [],
    isLoaded: loc.isLoaded !== false,
  }))

  const total = summaries.reduce((sum, s) => sum + s.total, 0)
  const monthlyTotal = summaries.reduce((sum, s) => sum + s.monthlyTotal, 0)
  const tabItemCounts = {
    today: summaries.reduce((sum, s) => sum + s.tabItemCounts.today, 0),
    monthly: summaries.reduce((sum, s) => sum + s.tabItemCounts.monthly, 0),
  }
  const tabTotals = {today: total, monthly: monthlyTotal}

  return (
    <FullHeightSummary>
      <OrderSummaryComponent
        locations={renderLocations}
        total={total}
        tabs={DEFAULT_TABS}
        country={country || 'US'}
        isUpgrade={isUpgrade}
        dueTodayIncludesTax={dueTodayIncludesTax}
        tabItemCounts={tabItemCounts}
        tabTotals={tabTotals}
        singleLocation={!isParentPay}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        openLocations={isParentPay ? openLocations : {0: true}}
        onToggleLocation={i =>
          setOpenLocations(prev => ({...prev, [i]: !prev[i]}))
        }
        softwareOpen={softwareOpen}
        addonsOpen={addonsOpen}
        oneTimeFeeOpen={oneTimeFeeOpen}
        onToggleSoftware={i =>
          setSoftwareOpen(prev => ({...prev, [i]: !prev[i]}))
        }
        onToggleAddons={i => setAddonsOpen(prev => ({...prev, [i]: !prev[i]}))}
        onToggleOneTimeFee={i =>
          setOneTimeFeeOpen(prev => ({...prev, [i]: !prev[i]}))
        }
        onToggleCoupons={i =>
          setCouponsOpen(prev => ({...prev, [i]: !prev[i]}))
        }
        couponsOpen={couponsOpen}
        flushEdges={true}
      />
    </FullHeightSummary>
  )
}
